/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { useCallback, useState, type ChangeEvent, type SyntheticEvent, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type FeatureFlag } from '../../lib/api/sa.js';
import { useFeatureFlags, useSetFeatureFlag } from '../../lib/queries/sa.js';

const INPUT_CLASS =
  'focus-ring w-full max-w-xs rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';

interface RowProps {
  readonly flag: FeatureFlag;
  readonly onToggle: (key: string, enabled: boolean) => void;
  readonly disabled: boolean;
}

function FlagRow({ flag, onToggle, disabled }: RowProps): JSX.Element {
  const { t } = useTranslation();
  const toggle = useCallback(() => {
    onToggle(flag.key, !flag.enabled);
  }, [onToggle, flag.key, flag.enabled]);
  return (
    <tr className="border-b border-slate-100 last:border-0 dark:border-slate-800">
      <td className="py-3 pr-4 font-medium text-slate-800 dark:text-slate-100">{flag.key}</td>
      <td className="py-3 pr-4">
        <Badge
          label={flag.enabled ? t('sa.settings.enabled') : t('sa.settings.disabled')}
          tone={flag.enabled ? 'green' : 'slate'}
        />
      </td>
      <td className="py-3 pr-4 text-slate-500 dark:text-slate-400">
        {flag.updatedAt.slice(0, 10)}
      </td>
      <td className="py-3 text-right">
        <Button
          variant="outline"
          size="sm"
          onClick={toggle}
          disabled={disabled}
          aria-pressed={flag.enabled}
        >
          {flag.enabled ? t('sa.settings.disable') : t('sa.settings.enable')}
        </Button>
      </td>
    </tr>
  );
}

function AddFlagForm({
  onAdd,
  disabled,
}: {
  readonly onAdd: (key: string) => void;
  readonly disabled: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [key, setKey] = useState('');
  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setKey(e.target.value);
  }, []);
  const submit = useCallback(
    (e: SyntheticEvent<HTMLFormElement>) => {
      e.preventDefault();
      const trimmed = key.trim();
      if (trimmed !== '') {
        onAdd(trimmed);
        setKey('');
      }
    },
    [key, onAdd],
  );
  return (
    <form onSubmit={submit} className="flex items-center gap-3">
      <input
        value={key}
        onChange={onChange}
        placeholder={t('sa.settings.addPlaceholder')}
        aria-label={t('sa.settings.addKey')}
        className={INPUT_CLASS}
      />
      <Button type="submit" disabled={disabled}>
        {t('sa.settings.add')}
      </Button>
    </form>
  );
}

const COLS = ['key', 'state', 'updated'] as const;

/** Super Admin → Settings. Platform feature flags (global enablement). */
export function SaSettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const flags = useFeatureFlags();
  const setFlag = useSetFeatureFlag();
  const rows = flags.data ?? [];

  const onToggle = useCallback(
    (key: string, enabled: boolean) => {
      setFlag.mutate({ key, enabled });
    },
    [setFlag],
  );
  const onAdd = useCallback(
    (key: string) => {
      setFlag.mutate({ key, enabled: true });
    },
    [setFlag],
  );

  return (
    <div className="space-y-6 p-8">
      <PageHeader title={t('sa.settings.title')} subtitle={t('sa.settings.subtitle')} />
      <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
        <h2 className="mb-4 text-sm font-semibold text-slate-700 dark:text-slate-200">
          {t('sa.settings.flags')}
        </h2>
        <QueryState
          isLoading={flags.isLoading}
          isError={flags.isError}
          isEmpty={rows.length === 0}
          loadingLabel={t('common.loading')}
          errorLabel={t('common.loadError')}
          emptyLabel={t('sa.settings.empty')}
        >
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400 dark:border-slate-700">
                {COLS.map((col) => (
                  <th key={col} className="pb-2 pr-4 font-medium">
                    {t(`sa.settings.col.${col}`)}
                  </th>
                ))}
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((flag) => (
                <FlagRow
                  key={flag.key}
                  flag={flag}
                  onToggle={onToggle}
                  disabled={setFlag.isPending}
                />
              ))}
            </tbody>
          </table>
        </QueryState>
        <div className="mt-6 border-t border-slate-100 pt-6 dark:border-slate-800">
          <p className="mb-3 text-sm font-medium text-slate-700 dark:text-slate-300">
            {t('sa.settings.addKey')}
          </p>
          <AddFlagForm onAdd={onAdd} disabled={setFlag.isPending} />
        </div>
      </div>
    </div>
  );
}
